import type {
  IRainToolActivity,
  TRainToolActivityLabelResolver,
} from '../../hooks/types';
import type { TRainValueJs } from '../../utils/value';
import { EQuillVisibleTool } from '../quill';
import { asHostLabel, asShortLabel } from './labels';

/** Namespace quill2's chat copy lives in. */
const NS = 'automation';

/**
 * A resolver that echoes one argument, keyed by status — every quill activity
 * has a `running` / `success` / `error` copy family taking a single value.
 */
const argLabel = (
  keyPrefix: string,
  argName: string,
  format: (
    value: TRainValueJs | undefined,
  ) => string | undefined = asShortLabel,
): TRainToolActivityLabelResolver => {
  return ({ status, args }) => {
    const value = format(args[argName]);
    if (!value) return undefined;
    return {
      key: `automation.quill2.activity.${keyPrefix}.${status}`,
      ns: NS,
      values: { value },
    };
  };
};

/** A resolver with no argument to echo — the copy is the whole row. */
const staticLabel =
  (keyPrefix: string): TRainToolActivityLabelResolver =>
  ({ status }) => ({
    key: `automation.quill2.activity.${keyPrefix}.${status}`,
    ns: NS,
  });

/**
 * A discovery browse, by the term it searched for. The connection id is the only
 * other argument and it is opaque, so the search term is all there is to name;
 * without one, quill is listing everything the connection offers.
 */
const browseDiscoverablesLabel: TRainToolActivityLabelResolver = ({
  status,
  args,
}) => {
  const value = asShortLabel(args.search);
  return value
    ? {
        key: `automation.quill2.activity.browseEntities.${status}`,
        ns: NS,
        values: { value },
      }
    : { key: `automation.quill2.activity.browseAllEntities.${status}`, ns: NS };
};

/**
 * Quill's activity rows: tools whose result never reaches the UI, but whose
 * having happened the user should still see.
 */
export const QUILL_TOOL_ACTIVITIES: Readonly<
  Record<string, IRainToolActivity>
> = {
  [EQuillVisibleTool.SearchProcedures]: {
    icon: 'Search',
    label: argLabel('searchProcedures', 'query'),
  },
  [EQuillVisibleTool.SearchApps]: {
    icon: 'Blocks',
    label: argLabel('searchApps', 'query'),
  },
  [EQuillVisibleTool.WebSearch]: {
    icon: 'Globe',
    label: argLabel('webSearch', 'query'),
  },
  [EQuillVisibleTool.WebFetch]: {
    icon: 'Link',
    label: argLabel('webFetch', 'url', asHostLabel),
  },
  [EQuillVisibleTool.PrepareCodeManifest]: {
    icon: 'FileCheck',
    label: staticLabel('prepareCodeManifest'),
  },
  // The step before a `set_discovered` approval card, so the row explains the
  // pause the user sat through before being asked to decide.
  [EQuillVisibleTool.BrowseDiscoverables]: {
    icon: 'Blocks',
    label: browseDiscoverablesLabel,
  },
  // Disclosure rather than progress: these read the user's own data. Labels are
  // static because the only argument is an AIP-160 filter expression.
  [EQuillVisibleTool.ListCollections]: {
    icon: 'Database',
    label: staticLabel('listCollections'),
  },
  [EQuillVisibleTool.ListAssets]: {
    icon: 'Folder',
    label: staticLabel('listAssets'),
  },
};
